/**
 * Purchase Management Types
 * 
 * Types for supplier management, purchase orders, purchase bills, and purchase returns
 */

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

export interface Supplier {
  id: string;
  businessName: string;
  contactPerson?: string;
  gstin?: string;
  address: Address;
  phone: string;
  email?: string;
  paymentTerms: number; // days
  performanceRating: number;
  supplierMetadata: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  
  // Calculated fields
  totalPurchases?: number;
  outstandingAmount?: number;
  lastPurchaseDate?: string;
}

export interface AdditionalCharge {
  chargeTypeId?: string;
  chargeType: string;
  description: string;
  amount: number;
  calculationType: 'fixed' | 'percentage';
  isTaxable: boolean;
  taxRate?: number;
  taxAmount?: number;
}

export interface PurchaseOrderItem {
  id: string;
  purchaseOrderId: string;
  variantId: string;
  variant?: {
    id: string;
    name: string;
    sku: string;
    itemCode: string;
    currentStock: number;
    product?: {
      name: string;
      hsnCode?: string;
      gstRate?: number;
    };
  };
  // Direct fields from backend for display
  productName?: string;
  variantName?: string;
  variantSku?: string;
  quantity: number;
  unitPrice: number;
  receivedQuantity: number;
  discountPercent: number;
  discountAmount: number;
  taxableAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  itemMetadata: any;
  createdAt: string;
}

export enum PurchaseOrderStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  ACKNOWLEDGED = 'acknowledged',
  PARTIAL_RECEIVED = 'partial_received',
  RECEIVED = 'received',
  BILLED = 'billed',
  CLOSED = 'closed'
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  supplier?: Supplier;
  supplierName?: string;  // Direct field from backend
  supplierGstin?: string; // Direct field from backend
  orderDate: string;
  expectedDelivery?: string;
  status: PurchaseOrderStatus;
  subtotal: number;
  discountAmount: number;
  shippingCharges: number;
  packingCharges: number;
  otherCharges: AdditionalCharge[];
  taxAmount: number;
  grandTotal: number;
  notes?: string;
  createdByAdminId: string;
  createdByAdminName?: string; // Direct field from backend
  createdByAdmin?: {
    id: string;
    name: string;
    email: string;
  };
  items: PurchaseOrderItem[];
  createdAt: string;
  updatedAt: string;
}

export enum PurchaseBillStatus {
  DRAFT = 'draft',
  RECEIVED = 'received',
  VERIFIED = 'verified',
  PAID = 'paid',
  PARTIAL = 'partial',
  OVERDUE = 'overdue'
}

export interface PurchaseBill {
  id: string;
  billNumber: string;
  purchaseOrderId?: string;
  purchaseOrder?: PurchaseOrder;
  supplierId: string;
  supplier?: Supplier;
  billDate: string;
  dueDate?: string;
  subtotal: number;
  discountAmount: number;
  shippingCharges: number;
  packingCharges: number;
  otherCharges: AdditionalCharge[];
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  status: PurchaseBillStatus;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  inputTaxCredit: number;
  billMetadata: any;
  createdByAdminId: string;
  createdAt: string;
  updatedAt: string;
}

export enum PurchaseReturnReason {
  DEFECTIVE = 'defective',
  WRONG_ITEM = 'wrong_item',
  EXCESS_QUANTITY = 'excess_quantity',
  QUALITY_ISSUES = 'quality_issues',
  OTHER = 'other'
}

export enum PurchaseReturnStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  ACKNOWLEDGED = 'acknowledged',
  PROCESSED = 'processed',
  CLOSED = 'closed'
}

export interface PurchaseReturnItem {
  id: string;
  purchaseReturnId: string;
  variantId: string;
  variant?: {
    id: string;
    name: string;
    sku: string;
    itemCode: string;
    product?: {
      name: string;
      hsnCode?: string;
      gstRate?: number;
    };
  };
  quantity: number;
  unitPrice: number;
  taxableAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  createdAt: string;
}

export interface PurchaseReturn {
  id: string;
  returnNumber: string;
  purchaseBillId: string;
  purchaseBill?: PurchaseBill;
  supplierId: string;
  supplier?: Supplier;
  returnDate: string;
  returnReason: PurchaseReturnReason;
  returnDescription?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: PurchaseReturnStatus;
  debitNoteNumber?: string;
  returnMetadata: any;
  createdByAdminId: string;
  items: PurchaseReturnItem[];
  createdAt: string;
  updatedAt: string;
}

export interface ChargeType {
  id: string;
  name: string;
  description?: string;
  chargeCode: string;
  isTaxable: boolean;
  defaultRate: number;
  calculationType: 'fixed' | 'percentage';
  appliesTo: 'sales' | 'purchase' | 'both';
  isActive: boolean;
}

export interface DiscountRule {
  id: string;
  name: string;
  discountCode: string;
  discountType: 'percentage' | 'fixed' | 'tiered';
  value: number;
  minQuantity?: number;
  minAmount?: number;
  customerType?: string;
  appliesTo: 'product' | 'category' | 'order';
  validFrom?: string;
  validTo?: string;
  isActive: boolean;
}

// API Request/Response types
export interface CreateSupplierRequest {
  businessName: string;
  contactPerson?: string;
  gstin?: string;
  address: Address;
  phone: string;
  email?: string;
  paymentTerms: number;
  supplierMetadata?: any;
}

export interface UpdateSupplierRequest extends Partial<CreateSupplierRequest> {
  isActive?: boolean;
}

export interface CreatePurchaseOrderRequest {
  supplierId: string;
  orderDate: string;
  expectedDelivery?: string;
  items: {
    variantId: string;
    quantity: number;
    unitPrice: number;
    discountPercent?: number;
  }[];
  shippingCharges?: number;
  packingCharges?: number;
  otherCharges?: AdditionalCharge[];
  notes?: string;
}

export interface UpdatePurchaseOrderRequest extends Partial<CreatePurchaseOrderRequest> {
  status?: PurchaseOrderStatus;
}

export interface GoodsReceiptRequest {
  items: {
    purchaseOrderItemId: string;
    receivedQuantity: number;
  }[];
  notes?: string;
}

export interface CreatePurchaseBillRequest {
  purchaseOrderId?: string;
  supplierId: string;
  billNumber: string;
  billDate: string;
  dueDate?: string;
  items: {
    variantId: string;
    quantity: number;
    unitPrice: number;
    discountPercent?: number;
  }[];
  shippingCharges?: number;
  packingCharges?: number;
  otherCharges?: AdditionalCharge[];
  billMetadata?: any;
}

export interface CreatePurchaseReturnRequest {
  purchaseBillId: string;
  returnDate: string;
  returnReason: PurchaseReturnReason;
  returnDescription?: string;
  items: {
    variantId: string;
    quantity: number;
    unitPrice: number;
  }[];
}

// List/Filter types
export interface SupplierListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PurchaseOrderListParams {
  page?: number;
  limit?: number;
  search?: string;
  supplierId?: string;
  status?: PurchaseOrderStatus;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PurchaseBillListParams {
  page?: number;
  limit?: number;
  search?: string;
  supplierId?: string;
  status?: PurchaseBillStatus;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PurchaseReturnListParams {
  page?: number;
  limit?: number;
  search?: string;
  supplierId?: string;
  status?: PurchaseReturnStatus;
  returnReason?: PurchaseReturnReason;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Response types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SupplierListResponse extends PaginatedResponse<Supplier> {}

// Purchase Order List Response - matches backend schema
export interface PurchaseOrderListResponse {
  purchase_orders: PurchaseOrder[];
  total: number;
  skip: number;
  limit: number;
}

// Purchase Bill List Response - matches backend schema
export interface PurchaseBillListResponse {
  purchase_bills: PurchaseBill[];
  total: number;
  skip: number;
  limit: number;
}

// Purchase Return List Response - matches backend schema
export interface PurchaseReturnListResponse {
  purchase_returns: PurchaseReturn[];
  total: number;
  skip: number;
  limit: number;
}

// Dashboard/Analytics types
export interface PurchaseDashboardData {
  totalSuppliers: number;
  activeSuppliers: number;
  pendingOrders: number;
  overduePayments: number;
  monthlyPurchaseAmount: number;
  topSuppliers: {
    supplier: Supplier;
    totalAmount: number;
    orderCount: number;
  }[];
  recentOrders: PurchaseOrder[];
  paymentsDue: PurchaseBill[];
}

export interface SupplierPerformance {
  supplierId: string;
  supplierName: string;
  totalOrders: number;
  totalAmount: number;
  onTimeDeliveryRate: number;
  averageDeliveryDays: number;
  qualityRating: number;
  lastOrderDate: string;
}